import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaCar, FaTools, FaCarBattery, FaTachometerAlt, FaCheckCircle, FaWrench, FaCarSide, FaWind, FaRulerCombined } from 'react-icons/fa';
import PageTransition from '../components/PageTransition';

const SERVICES_LIST = [
    {
      id: 'karosserie',
      icon: <FaCar />,
      title: 'Karosserie & Lack',
      description: 'Professionelle Karosseriearbeiten und Lackierungen in unserer hauseigenen Werkstatt. Von der kleinen Delle bis zum Rahmenschaden – wir stellen den Originalzustand wieder her.',
      features: []
    },
    {
      id: 'elektro',
      icon: <FaCarBattery />,
      title: 'E-Mobilität & Tesla',
      description: 'Als zertifizierter Tesla-Bodyshop sind wir auf die Reparatur von Elektrofahrzeugen spezialisiert. Unsere Mitarbeiter sind für Hochvoltsysteme qualifiziert.',
      features: []
    },
    {
      id: 'aluminium',
      icon: <FaTools />,
      title: 'Aluminium-Instandsetzung',
      description: 'Die Bearbeitung von Aluminium erfordert spezielle Werkzeuge und Know-how. Wir verfügen über einen isolierten Aluminium-Arbeitsplatz.',
      features: []
    },
    {
      id: 'vermessung',
      icon: <FaTachometerAlt />,
      title: '3D-Vermessung',
      description: 'Mit modernster 3D-Messtechnik analysieren wir die Karosseriestruktur und das Fahrwerk auf den Millimeter genau.',
      features: []
    },
    {
      id: 'begutachtung',
      icon: <FaCheckCircle />,
      title: '§57a Begutachtung',
      description: 'Wir führen die gesetzlich vorgeschriebene "Pickerl"-Überprüfung für Ihren PKW (bis 3,5t) und leichte Anhänger durch.',
      features: []
    },
    {
      id: 'wartung',
      icon: <FaWrench />,
      title: 'Service & Wartung',
      description: 'Servicearbeiten nach Herstellervorgaben unter Verwendung von Originalteilen – damit Ihre Herstellergarantie erhalten bleibt.',
      features: []
    },
    {
      id: 'mobilitaet',
      icon: <FaCarSide />,
      title: 'Ersatzmobilität',
      description: 'Damit Sie auch während der Reparatur mobil bleiben, stellen wir Ihnen gerne eines unserer Ersatzfahrzeuge zur Verfügung.',
      features: []
    },
    {
      id: 'raeder',
      icon: <FaCarSide />,
      title: 'Reifen & Räder',
      description: 'Vom saisonalen Radwechsel über die Einlagerung bis hin zur Montage neuer Reifen – wir kümmern uns um den Kontakt zur Straße.',
      features: []
    },
    {
      id: 'glas',
      icon: <FaWind />,
      title: 'Autoglas',
      description: 'Steinschlagreparatur oder Scheibentausch. Wir verbauen Originalscheiben und kalibrieren Ihre Assistenzsysteme neu.',
      features: []
    },
    {
      id: 'fahrwerk',
      icon: <FaRulerCombined />,
      title: 'Fahrwerkstechnik',
      description: 'Ein perfekt eingestelltes Fahrwerk sorgt für Sicherheit und weniger Reifenverschleiß. Wir prüfen und justieren Stoßdämpfer und Federn.',
      features: []
    }
];

const Services: React.FC = () => {
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    let rafId = 0;
    let lastActive = -1;

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (window.matchMedia('(max-width: 991.98px)').matches) return;
        const scrollPosition = window.scrollY + window.innerHeight / 3;
        let next = lastActive >= 0 ? lastActive : 0;

        for (let index = 0; index < SERVICES_LIST.length; index++) {
          const section = document.getElementById(SERVICES_LIST[index].id);
          if (!section) continue;
          const offsetTop = section.offsetTop;
          const offsetHeight = section.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            next = index;
            break;
          }
        }

        if (next !== lastActive) {
          lastActive = next;
          setActiveService(next);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const scrollToService = (id: string, index: number) => {
    setActiveService(index);
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Space for sticky header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <PageTransition>
      <div className="min-vh-100 pt-5" style={{ backgroundColor: 'var(--color-bg-warm)' }}>

      <Container className="pb-5">
        <Row>
          {/* Sticky Sidebar Navigation (Desktop) */}
          <Col lg={4} className="d-none d-lg-block">
            <div className="sticky-top" style={{ top: '120px', height: 'calc(100vh - 140px)', overflowY: 'hidden', zIndex: 10, display: 'flex', flexDirection: 'column' }}>
              <div className="bg-white rounded-4 p-2 flex-grow-1" style={{ overflowY: 'auto', minHeight: 0, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                {SERVICES_LIST.map((service, index) => (
                  <motion.button
                    key={service.id}
                    onClick={() => scrollToService(service.id, index)}
                    className={`w-100 text-start border-0 p-3 rounded-3 mb-1 d-flex align-items-center transition-all ${
                      activeService === index ? 'bg-light' : 'bg-transparent'
                    }`}
                    style={{
                      color: activeService === index ? '#FC5000' : '#333',
                      fontWeight: activeService === index ? '600' : '400',
                      transition: 'all 0.2s ease'
                    }}
                    whileHover={{ x: 4, backgroundColor: 'rgba(0,0,0,0.02)' }}
                  >
                    <div 
                      className="me-3 d-flex align-items-center justify-content-center rounded-circle"
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        backgroundColor: activeService === index ? '#FC5000' : '#f8f9fa',
                        color: activeService === index ? 'white' : '#6c757d',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {React.cloneElement(service.icon as React.ReactElement, { size: 14 })}
                    </div>
                    {service.title}
                    {activeService === index && (
                      <motion.div 
                        layoutId="activeIndicator"
                        className="ms-auto" 
                        style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#FC5000' }}
                      />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>
          </Col>

          {/* Services List */}
          <Col lg={8}>
            <div className="d-flex flex-column gap-5">
              {SERVICES_LIST.map((service, index) => (
                <motion.div
                  key={service.id}
                  id={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-4 p-4 p-md-5 position-relative overflow-hidden"
                  style={{ zIndex: 0, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
                >
                  <Row className="position-relative z-1">
                    <Col md={12}>
                      <div className="d-flex align-items-center mb-4">
                        <div 
                          className="d-flex align-items-center justify-content-center rounded-3 me-3"
                          style={{ 
                            width: '60px', 
                            height: '60px', 
                            backgroundColor: 'rgba(252, 80, 0, 0.1)', 
                            color: '#FC5000' 
                          }}
                        >
                          {React.cloneElement(service.icon as React.ReactElement, { size: 28 })}
                        </div>
                        <div>
                          <h2 className="h3 fw-bold mb-1">{service.title}</h2>
                          <div style={{ width: '40px', height: '3px', backgroundColor: '#FC5000', borderRadius: '2px' }} />
                        </div>
                      </div>
                      
                      <p className="lead text-muted mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.7' }}>
                        {service.description}
                      </p>

                      {service.features && service.features.length > 0 && (
                        <div className="bg-light rounded-3 p-4">
                          <h6 className="fw-bold text-dark mb-3 text-uppercase small" style={{ letterSpacing: '1px' }}>Leistungsübersicht</h6>
                          <Row className="g-3">
                            {service.features.map((feature, i) => (
                              <Col sm={6} key={i}>
                                <div className="d-flex align-items-center text-secondary">
                                  <div 
                                    className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                                    style={{ width: '18px', height: '18px', backgroundColor: '#28a745', color: 'white', fontSize: '10px' }}
                                  >
                                    ✓
                                  </div>
                                  {feature}
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}
                    </Col>
                  </Row>
                </motion.div>
              ))}
            </div>
          </Col>
        </Row>
      </Container>
      </div>
    </PageTransition>
  );
};

export default Services;
